/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, type FunctionComponent } from 'react'

import { EuiSkeletonRectangle, EuiSpacer, EuiText } from '@elastic/eui'

import withBillingNotificationsCenter from '@modules/billing-components/BillingNotificationsCenter/withBillingNotificationsCenter'
import { FormattedMessage } from '@modules/i18n'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isPrepaidConsumptionCustomer } from '@modules/billing-lib/billingDetails/utils'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import Budgets from './Budgets'
import PrepaidConsumption from './PrepaidConsumption'

const BillingNotifications: FunctionComponent = () => {
  const billingDetails = useBillingDetails()
  const organizationId = useOrganizationId()

  if (!organizationId || !billingDetails.data) {
    return <EuiSkeletonRectangle data-test-subj='billing.notifications.loading' />
  }

  const showCreditConsumptionEmail = isPrepaidConsumptionCustomer(billingDetails.data)

  return (
    <Fragment>
      <EuiText size='s' color='subdued'>
        <FormattedMessage
          id='billing.notifications.page.description'
          defaultMessage="Manage email notifications about your organization's usage and costs. You can set a budget to get alerts about your spending and control other billing-related communications."
        />
      </EuiText>
      <EuiSpacer size='l' />
      <Budgets />
      {showCreditConsumptionEmail && (
        <Fragment>
          <EuiSpacer size='l' />
          <PrepaidConsumption organizationId={organizationId} />
        </Fragment>
      )}
    </Fragment>
  )
}

export default withBillingNotificationsCenter(BillingNotifications)
